import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ShoppingCart } from "lucide-react";
import { NavLink, Link } from "react-router-dom";
import { cn } from "../lib/utils";

const sansiLogo = "/images/sansi-logo.png";

interface NavbarProps {
  cartCount: number;
  onOpenCart: () => void;
}

export function Navbar({ cartCount, onOpenCart }: NavbarProps) {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isIOS, setIsIOS] = useState(false);

  useEffect(() => {
    // Check if device is iOS
    const checkIsIOS = () => {
      const userAgent = window.navigator.userAgent.toLowerCase();
      const isIphone = /iphone|ipod|ipad/.test(userAgent);
      const isMac = /mac/.test(userAgent) && navigator.maxTouchPoints > 0;
      return isIphone || isMac;
    };
    setIsIOS(checkIsIOS());
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <>
      {/* Desktop Top Navbar */}
      <nav className="hidden md:block fixed top-0 left-0 right-0 z-50">
        <div className="container mx-auto px-6">
          <div className="bg-black/90 backdrop-blur-md border border-t-0 border-white/10 shadow-[0_2px_20px_rgba(0,0,0,0.4)] px-6 py-4 flex items-center justify-between">
            {/* Logo - Left */}
            {/* Logo - Left */}
            <Link
              to="/"
              onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
              className="flex items-center gap-3 shrink-0 group"
            >
              <img
                src={sansiLogo}
                alt="Sansi Eco Foods"
                className="h-16 w-auto object-contain group-hover:scale-105 transition-transform duration-300"
              />
              <span className="text-lg lg:text-xl font-bold tracking-wider font-display uppercase whitespace-nowrap flex gap-2">
                <span className="text-transparent bg-clip-text bg-linear-to-r from-sansi-orange to-sansi-green">
                  SANSI
                </span>
                <span className="text-sansi-green">ECO</span>
                <span className="text-transparent bg-clip-text bg-linear-to-r from-sansi-orange to-sansi-green">
                  FOODS
                </span>
              </span>
            </Link>

            {/* Navigation Items - Center */}
            <div className="absolute left-1/2 transform -translate-x-1/2 flex items-center gap-8">
              <NavLink
                to="/"
                className={({ isActive }) =>
                  cn(
                    "text-sm font-bold uppercase tracking-wide transition-all duration-300",
                    isActive
                      ? "text-sansi-green"
                      : "text-white hover:text-sansi-green"
                  )
                }
              >
                Store
              </NavLink>

              <NavLink
                to="/our-story"
                className={({ isActive }) =>
                  cn(
                    "text-sm font-bold uppercase tracking-wide transition-all duration-300",
                    isActive
                      ? "text-sansi-green"
                      : "text-white hover:text-sansi-green"
                  )
                }
              >
                Our Story
              </NavLink>

              <NavLink
                to="/contact"
                className={({ isActive }) =>
                  cn(
                    "text-sm font-bold uppercase tracking-wide transition-all duration-300",
                    isActive
                      ? "text-sansi-green"
                      : "text-white hover:text-sansi-green"
                  )
                }
              >
                Contact
              </NavLink>
            </div>

            {/* Cart Button - Right */}
            <button
              onClick={onOpenCart}
              className="group relative flex items-center gap-2 px-5 py-2.5 bg-sansi-green text-black font-bold text-sm uppercase tracking-wider hover:bg-white transition-all duration-300 transform hover:-translate-y-1 hover:shadow-[4px_4px_0px_var(--color-sansi-orange)]"
            >
              <ShoppingCart className="w-4 h-4" />
              <span>Cart</span>
              {cartCount > 0 && (
                <span className="absolute -top-2 -right-2 bg-sansi-orange text-white text-[10px] font-bold w-5 h-5 flex items-center justify-center rounded-full shadow-md">
                  {cartCount}
                </span>
              )}
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Top Logo */}
      <div className="md:hidden fixed top-6 left-0 right-0 z-50 pointer-events-none">
        {isIOS ? (
          <AnimatePresence mode="wait">
            {!isScrolled ? (
              <motion.div
                key="center-logo"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                transition={{ duration: 0.2 }}
                className="flex justify-center w-full"
              >
                <div className="pointer-events-auto">
                  <Link
                    to="/"
                    onClick={() =>
                      window.scrollTo({ top: 0, behavior: "smooth" })
                    }
                    className="flex items-center gap-3"
                  >
                    <img
                      src={sansiLogo}
                      alt="Sansi Eco Foods"
                      className="h-16 w-auto object-contain drop-shadow-md shrink-0"
                    />
                    <span className="text-lg font-bold tracking-wider font-display shadow-black drop-shadow-sm flex gap-1.5 whitespace-nowrap">
                      <span className="text-transparent bg-clip-text bg-linear-to-r from-sansi-orange to-sansi-green">
                        SANSI
                      </span>
                      <span className="text-sansi-green">ECO</span>
                      <span className="text-transparent bg-clip-text bg-linear-to-r from-sansi-orange to-sansi-green">
                        FOODS
                      </span>
                    </span>
                  </Link>
                </div>
              </motion.div>
            ) : (
              <motion.div
                key="left-logo"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.2 }}
                className="flex justify-start px-6 w-full"
              >
                <div className="pointer-events-auto">
                  <Link
                    to="/"
                    onClick={() =>
                      window.scrollTo({ top: 0, behavior: "smooth" })
                    }
                    className="flex items-center gap-3"
                  >
                    <img
                      src={sansiLogo}
                      alt="Sansi Eco Foods"
                      className="h-16 w-auto object-contain drop-shadow-md shrink-0"
                    />
                  </Link>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        ) : (
          <div
            className={cn(
              "flex px-6 transition-all duration-500",
              isScrolled ? "justify-start" : "justify-center"
            )}
          >
            <motion.div
              layout
              className="flex items-center gap-3 pointer-events-auto"
              transition={{ type: "spring", stiffness: 300, damping: 30 }}
            >
              <Link
                to="/"
                onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
                className="flex items-center gap-3"
              >
                <motion.img
                  layout
                  src={sansiLogo}
                  alt="Sansi Eco Foods"
                  className="h-16 w-auto object-contain drop-shadow-md shrink-0"
                />
                <AnimatePresence>
                  {!isScrolled && (
                    <motion.span
                      initial={{ opacity: 0, width: 0 }}
                      animate={{ opacity: 1, width: "auto" }}
                      exit={{ opacity: 0, width: 0 }}
                      transition={{ duration: 0.3 }}
                      className="text-lg font-bold tracking-wider font-display shadow-black drop-shadow-sm flex gap-1.5 whitespace-nowrap overflow-hidden"
                    >
                      <span className="text-transparent bg-clip-text bg-linear-to-r from-sansi-orange to-sansi-green">
                        SANSI
                      </span>
                      <span className="text-sansi-green">ECO</span>
                      <span className="text-transparent bg-clip-text bg-linear-to-r from-sansi-orange to-sansi-green">
                        FOODS
                      </span>
                    </motion.span>
                  )}
                </AnimatePresence>
              </Link>
            </motion.div>
          </div>
        )}
      </div>

      {/* Mobile Bottom Floating Nav Pill */}
      <nav className="md:hidden fixed bottom-8 left-1/2 transform -translate-x-1/2 z-50">
        <div className="bg-[#1a1a1a]/90 backdrop-blur-xl border border-white/10 rounded-none px-2 py-2 flex items-center shadow-[0_8px_32px_rgba(0,0,0,0.5)]">
          <div className="flex items-center gap-1 px-2">
            <NavLink
              to="/"
              className={({ isActive }) =>
                cn(
                  "px-5 py-3 rounded-none text-sm font-bold uppercase transition-all duration-300",
                  isActive
                    ? "bg-sansi-green text-black shadow-lg"
                    : "text-gray-400 hover:text-white hover:bg-white/5"
                )
              }
            >
              Store
            </NavLink>

            <NavLink
              to="/our-story"
              className={({ isActive }) =>
                cn(
                  "px-5 py-3 rounded-none text-sm font-bold uppercase transition-all duration-300",
                  isActive
                    ? "bg-white text-black shadow-lg"
                    : "text-gray-400 hover:text-white hover:bg-white/5"
                )
              }
            >
              Story
            </NavLink>

            <NavLink
              to="/contact"
              className={({ isActive }) =>
                cn(
                  "px-5 py-3 rounded-none text-sm font-bold uppercase transition-all duration-300",
                  isActive
                    ? "bg-sansi-orange text-black shadow-lg"
                    : "text-gray-400 hover:text-white hover:bg-white/5"
                )
              }
            >
              Contact
            </NavLink>
          </div>

          <div className="w-px h-8 bg-white/10 mx-2"></div>

          <button
            onClick={onOpenCart}
            className="relative p-3 rounded-none hover:bg-white/10 transition-colors group mr-1"
          >
            <ShoppingCart className="w-5 h-5 text-white group-hover:text-sansi-orange transition-colors" />
            {cartCount > 0 && (
              <span className="absolute top-1 right-1 bg-sansi-orange text-white text-[10px] font-bold w-4 h-4 flex items-center justify-center rounded-full">
                {cartCount}
              </span>
            )}
          </button>
        </div>
      </nav>
    </>
  );
}
